import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import type { Store } from '@jane/shared/types';

import {
  getLocalCartUuid,
  kioskAuthenticationQueryKeys,
  useGetCartForUser,
  useKioskDeviceGrant,
  useKioskDeviceSession,
} from '../../data-access';
import type { DeviceGrant } from '../../data-access/lib/authentication/authentication.types';
import { getUsername } from '../../util';

export const useKioskUserSession = ({ store }: { store: Store }) => {
  const queryClient = useQueryClient();
  const cartUuid = getLocalCartUuid();
  const [signedIn, setSignedIn] = useState(false);
  const [deviceGrant, setDeviceGrant] = useState<DeviceGrant | null>(null);
  const [qrCodeError, setQRCodeError] = useState<string | null>(null);

  const { mutate: createDeviceGrant, isLoading } = useKioskDeviceGrant({
    onSuccess: (grant: DeviceGrant) => {
      setDeviceGrant(grant);
    },
    onError: () => {
      setQRCodeError(
        'We could not generate your QR code, please go back and try again. If this problem persists, please check out as a guest and let a budtender know.'
      );
    },
  });

  useEffect(() => {
    createDeviceGrant();
  }, []);

  const { data: kioskUser, error } = useKioskDeviceSession(
    {
      deviceCode: deviceGrant?.device_code,
    },
    {
      enabled: !signedIn && !!deviceGrant,
      onSuccess: () => {
        setSignedIn(true);
      },
    }
  );

  useGetCartForUser(
    {
      app_mode: 'kiosk',
      uuid: cartUuid as string,
      store_id: store?.id,
    },
    {
      enabled: !!kioskUser && !!cartUuid,
      onSuccess: (cart) => {
        queryClient.setQueryData(['cart', cartUuid], cart);
        queryClient.invalidateQueries({ queryKey: ['cart', cartUuid] });
      },
    }
  );

  const userName = useMemo(() => getUsername(kioskUser), [kioskUser]);

  useEffect(() => {
    if (error?.message?.includes('code is invalid')) {
      queryClient.invalidateQueries({
        queryKey: kioskAuthenticationQueryKeys.deviceGrant(),
      });
    }
  }, [error]);

  return { deviceGrant, userName, signedIn, isLoading, qrCodeError };
};
